import React from 'react'
import { Box, Button, Stack } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn'
import { useProductForId } from 'api/tree.hooks'
import {
  markProductAsAssignable,
  markProductAsUnassignable,
} from 'components/resources/products/utility'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { allProducts } from 'api/masters.atoms'
import { useCurrentUserResource } from 'infrastructure/auth/auth.hooks'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'

export function AssignableColumn<T extends { id: number }>({
  item: { id },
}: {
  item: T
}) {
  const product = useProductForId(id)
  const assignable = !product?.unassignable_at

  if (!product) return <Box />
  return (
    <Box>
      {assignable ? (
        <CheckCircleIcon color="success" />
      ) : (
        <DoNotDisturbOnIcon color="error" />
      )}
    </Box>
  )
}

export function AssignableEdit<T extends { id: number }>({
  item: { id },
}: {
  item: T
}) {
  const currentUser = useCurrentUserResource()
  const markAssignable = useWithErrorNotification(
    { errorTitle: 'Error while marking as Assignable' },
    markProductAsAssignable,
  )
  const markUnassignable = useWithErrorNotification(
    { errorTitle: 'Error while marking as Unassignable' },
    markProductAsUnassignable,
  )
  const isSuperAdmin = currentUser?.roles_list.includes('super-admin')
  const productsRefresh = useSetAtom(allProducts)
  const product = useProductForId(id)
  const assignable = !product?.unassignable_at
  const markProduct = async () => {
    if (!isSuperAdmin || !product) return
    if (assignable) {
      await markUnassignable({ id: product.id })
      productsRefresh()
    } else {
      await markAssignable({ id: product.id })
      productsRefresh()
    }
  }

  if (!product) return <Box />
  return (
    <Box>
      {assignable ? (
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <CheckCircleIcon color="info" /> <span>Assignable</span>
        </Stack>
      ) : (
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <DoNotDisturbOnIcon color="info" /> <span>Unassignable</span>
        </Stack>
      )}
      {isSuperAdmin && (
        <Button onClick={markProduct}>
          {assignable ? 'Mark as Unassignable' : 'Mark as Assignable'}
        </Button>
      )}
    </Box>
  )
}
